import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { confirmDialog } from '../../components/ConfirmDialog';
import { compressCarrierData } from '../../utils/carrierHelpers';
import './AdminConfigsManagement.css';

function AdminConfigsManagement({ token }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [editingCells, setEditingCells] = useState({});
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [typeFilter, setTypeFilter] = useState('all'); // all, individual, group
  const [sortConfig, setSortConfig] = useState({ col: 'name', dir: 'asc' }); // unified sort state for all columns
  const [collapsedGroups, setCollapsedGroups] = useState(new Set()); // group identifiers that are collapsed

  // Channel config state
  const [channelConfig, setChannelConfig] = useState({ smsEnabled: true, emailEnabled: true });
  const [channelSaving, setChannelSaving] = useState(false);

  // ⭐ Refs for race-condition-free auto-save debounce
  const saveTimers = useRef({});
  const editingCellsRef = useRef({});
  const agentsRef = useRef([]);

  useEffect(() => {
    fetchAgents();
    fetchChannelConfig();
  }, []);

  // Keep agentsRef in sync so debounced saves always use latest agent data
  useEffect(() => { agentsRef.current = agents; }, [agents]);

  const fetchAgents = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // ⭐ Fetch only active agents (filter out pending signups)
      const response = await axios.get('/api/admin/agents/summary?accountStatus=active', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // ⭐ Additional client-side filter as safety measure - exclude pending agents
      const activeAgents = response.data.filter(agent => agent.accountStatus !== 'pending');
      
      setAgents(activeAgents);
      if (!silent) setLoading(false);
    } catch (error) {
      console.error('Error fetching agents:', error);
      if (!silent) toast.error('Error fetching agents');
      if (!silent) setLoading(false);
    }
  };

  const fetchChannelConfig = async () => {
    try {
      const r = await axios.get('/api/admin/channel-config', { headers: { Authorization: `Bearer ${token}` } });
      setChannelConfig({ smsEnabled: r.data.smsEnabled !== false, emailEnabled: r.data.emailEnabled !== false });
    } catch (e) { console.error('fetchChannelConfig', e); }
  };

  const saveChannelConfig = async (newCfg) => {
    setChannelSaving(true);
    try {
      await axios.put('/api/admin/channel-config', newCfg, { headers: { Authorization: `Bearer ${token}` } });
      setChannelConfig(newCfg);
      toast.success('Channel settings saved');
    } catch (e) { toast.error('Failed to save channel settings'); }
    finally { setChannelSaving(false); }
  };

  // Toggle SMS or Email channel for an individual agent
  const toggleAgentChannel = async (agent, channel) => {
    const saveKey = `${agent.ghlLocationId || agent.email}-channel`;
    setSaving(prev => ({ ...prev, [saveKey]: true }));
    try {
      const current = agent.channelConfig || {};
      const newVal = current[channel] !== false ? false : true; // toggle
      const updatedChannelConfig = { ...current, [channel]: newVal };
      const fullAgentRes = await axios.get(`/api/admin/agents/${encodeURIComponent(agent.email)}`, { headers: { Authorization: `Bearer ${token}` } });
      await axios.put(`/api/admin/agents/${encodeURIComponent(agent.email)}`, { ...fullAgentRes.data, channelConfig: updatedChannelConfig }, { headers: { Authorization: `Bearer ${token}` } });
      setAgents(prev => prev.map(a => a.email === agent.email ? { ...a, channelConfig: updatedChannelConfig } : a));
      toast.success(`${channel === 'smsEnabled' ? 'SMS' : 'Email'} ${newVal ? 'enabled' : 'disabled'} for ${agent.name || agent.email}`);
    } catch (e) { toast.error('Failed to update channel setting'); }
    finally { setSaving(prev => ({ ...prev, [saveKey]: false })); }
  };

  // Toggle SMS or Email channel for a group member
  const toggleMemberChannel = async (agent, member, channel) => {
    const saveKey = `${agent.ghlLocationId || agent.email}-${member.email}-channel`;
    setSaving(prev => ({ ...prev, [saveKey]: true }));
    try {
      const current = member.channelConfig || {};
      const newVal = current[channel] !== false ? false : true;
      const updatedChannelConfig = { ...current, [channel]: newVal };
      const fullAgentRes = await axios.get(`/api/admin/agents/${encodeURIComponent(agent.email)}`, { headers: { Authorization: `Bearer ${token}` } });
      const updatedMembers = fullAgentRes.data.groupMembers.map(m =>
        m.email === member.email ? { ...m, channelConfig: updatedChannelConfig } : m
      );
      await axios.put(`/api/admin/agents/${encodeURIComponent(agent.email)}`, { ...fullAgentRes.data, groupMembers: updatedMembers }, { headers: { Authorization: `Bearer ${token}` } });
      setAgents(prev => prev.map(a => a.email === agent.email
        ? { ...a, groupMembers: a.groupMembers.map(m => m.email === member.email ? { ...m, channelConfig: updatedChannelConfig } : m) }
        : a
      ));
      toast.success(`${channel === 'smsEnabled' ? 'SMS' : 'Email'} ${newVal ? 'enabled' : 'disabled'} for ${member.name || member.email}`);
    } catch (e) { toast.error('Failed to update channel setting'); }
    finally { setSaving(prev => ({ ...prev, [saveKey]: false })); }
  };

  const handleCellEdit = (identifier, field, value, type = 'agent', memberEmail = null) => {
    const key = memberEmail ? `${identifier}-${memberEmail}-${field}` : `${identifier}-${field}`;
    // ⭐ Update ref synchronously so debounced saves always read the freshest value
    editingCellsRef.current = { ...editingCellsRef.current, [key]: value };
    setEditingCells(prev => ({ ...prev, [key]: value }));
  };

  // ⭐ getCellValue reads from ref (always fresh, no stale closure issue)
  const getCellValue = (identifier, field, defaultValue, memberEmail = null) => {
    const key = memberEmail ? `${identifier}-${memberEmail}-${field}` : `${identifier}-${field}`;
    return editingCellsRef.current[key] !== undefined ? editingCellsRef.current[key] : defaultValue;
  };

  // ⭐ Auto-save with 1.5s debounce per row — called from each input onChange
  // Race-condition-safe: only one pending save per row at a time; always reads
  // from editingCellsRef (latest values) and agentsRef (latest agent object).
  const handleCellEditWithSave = (identifier, field, value, agentEmail, memberEmail = null) => {
    handleCellEdit(identifier, field, value, memberEmail ? 'member' : 'agent', memberEmail);
    const saveKey = memberEmail ? `${identifier}-${memberEmail}` : identifier;
    if (saveTimers.current[saveKey]) clearTimeout(saveTimers.current[saveKey]);
    saveTimers.current[saveKey] = setTimeout(() => {
      delete saveTimers.current[saveKey];
      const currentAgent = agentsRef.current.find(a => a.email === agentEmail);
      if (!currentAgent) return;
      if (memberEmail) {
        const currentMember = currentAgent.groupMembers?.find(m => m.email === memberEmail);
        if (currentMember) saveMemberConfig(currentAgent, currentMember);
      } else {
        saveAgentConfig(currentAgent);
      }
    }, 1500);
  };

  // Unified column sort handler — same column cycles asc→desc→null; new column starts asc
  const handleSort = (col) => {
    setSortConfig(prev => {
      if (prev.col === col) {
        if (prev.dir === 'asc')  return { col, dir: 'desc' };
        if (prev.dir === 'desc') return { col: null, dir: null };
      }
      return { col, dir: 'asc' };
    });
  };

  // Returns the arrow indicator for a given column header
  const sortArrow = (col) => {
    if (sortConfig.col !== col) return <span className="sort-arrow inactive">↕</span>;
    if (sortConfig.dir === 'asc')  return <span className="sort-arrow active">↑</span>;
    if (sortConfig.dir === 'desc') return <span className="sort-arrow active">↓</span>;
    return <span className="sort-arrow inactive">↕</span>;
  };

  // Auto-save toggle for agents - EXACT COPY FROM AdminAgents.js
  const toggleAgentStatus = async (agent) => {
    const currentStatus = agent.distributionConfig?.enabled !== false;
    const confirmed = await confirmDialog(
      `Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this ${agent.agentType === 'group' ? 'group' : 'agent'}?`
    );
    if (!confirmed) return;

    const identifier = agent.ghlLocationId || agent.email;
    setSaving(prev => ({ ...prev, [`${identifier}-toggle`]: true }));

    try {
      // ✅ FIX: Fetch full agent data first to preserve all fields (especially statesWithCarriers for groups)
      const fullAgentResponse = await axios.get(`/api/admin/agents/${encodeURIComponent(agent.email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fullAgent = fullAgentResponse.data;
      
      // Compress group members carrier data to reduce payload size
      let compressedAgent = { ...fullAgent };
      if (fullAgent.groupMembers && fullAgent.groupMembers.length > 0) {
        compressedAgent.groupMembers = fullAgent.groupMembers.map(member => ({
          ...member,
          statesWithCarriers: member.statesWithCarriers ? compressCarrierData(member.statesWithCarriers) : {}
        }));
      }
      
      // Update only the enabled status
      const updatedConfig = {
        ...compressedAgent.distributionConfig,
        enabled: !currentStatus
      };

      await axios.put(`/api/admin/agents/${encodeURIComponent(agent.email)}`, {
        ...compressedAgent,
        distributionConfig: updatedConfig
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setAgents(prev => prev.map(a => {
        if (a.email === agent.email) {
          return {
            ...a,
            distributionConfig: {
              ...a.distributionConfig,
              enabled: !currentStatus
            }
          };
        }
        return a;
      }));
      
      toast.success(`${agent.agentType === 'group' ? 'Group' : 'Agent'} ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling agent status:', error);
      toast.error('Error updating status');
    } finally {
      setSaving(prev => ({ ...prev, [`${identifier}-toggle`]: false }));
    }
  };

  // Auto-save toggle for group members - WITH CONFIRMATION AND PROPER COMPRESSION
  const toggleMemberStatus = async (agent, member) => {
    const currentStatus = member.enabled !== false;
    const confirmed = await confirmDialog(
      `Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} ${member.name}?`
    );
    if (!confirmed) return;

    const identifier = agent.ghlLocationId || agent.email;
    const memberKey = `${identifier}-${member.email}-toggle`;
    setSaving(prev => ({ ...prev, [memberKey]: true }));

    try {
      // ✅ Fetch full agent data first to preserve all fields
      const fullAgentResponse = await axios.get(`/api/admin/agents/${encodeURIComponent(agent.email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fullAgent = fullAgentResponse.data;
      const newStatus = !currentStatus;
      
      // Update member in groupMembers array
      const updatedMembers = fullAgent.groupMembers.map(m => {
        if (m.email === member.email) {
          return {
            ...m,
            enabled: newStatus
          };
        }
        return m;
      });

      // Compress carrier data - EXACT SAME AS AdminAgents.js
      const compressedMembers = updatedMembers.map(member => ({
        ...member,
        statesWithCarriers: member.statesWithCarriers ? compressCarrierData(member.statesWithCarriers) : {}
      }));

      await axios.put(`/api/admin/agents/${encodeURIComponent(agent.email)}`, {
        ...fullAgent,
        groupMembers: compressedMembers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setAgents(prev => prev.map(a => {
        if (a.email === agent.email && a.groupMembers) {
          return {
            ...a,
            groupMembers: a.groupMembers.map(m => {
              if (m.email === member.email) {
                return {
                  ...m,
                  enabled: newStatus
                };
              }
              return m;
            })
          };
        }
        return a;
      }));
      
      toast.success(`${member.name} ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling member status:', error);
      toast.error('Error updating member status');
    } finally {
      setSaving(prev => ({ ...prev, [memberKey]: false }));
    }
  };

  const saveAgentConfig = async (agent) => {
    console.log('saveAgentConfig called with agent:', {
      email: agent.email,
      ghlLocationId: agent.ghlLocationId,
      agentType: agent.agentType,
      name: agent.name,
      ghlLocationName: agent.ghlLocationName
    });
    
    if (!agent.email) {
      console.error('Agent has no email field:', agent);
      toast.error('Cannot save: Agent email is missing');
      return;
    }
    
    const identifier = agent.ghlLocationId || agent.email;
    setSaving(prev => ({ ...prev, [identifier]: true }));

    try {
      // Get edited values  
      const priority = parseInt(getCellValue(identifier, 'priority', agent.distributionConfig?.priority || 1)) || 1;
      const weightage = parseInt(getCellValue(identifier, 'weightage', agent.distributionConfig?.weightage || 1)) || 1;
      const dailyCap = parseInt(getCellValue(identifier, 'dailyCap', agent.distributionConfig?.dailyCap || 0)) || 0;
      const monthlyCap = parseInt(getCellValue(identifier, 'monthlyCap', agent.distributionConfig?.monthlyCap || 0)) || 0;
      const totalPurchased = parseInt(getCellValue(identifier, 'totalPurchased', agent.distributionConfig?.totalPurchased || 0)) || 0;

      // ✅ Validation (same as AdminAgents.js)
      if (agent.agentType === 'individual') {
        if (dailyCap > totalPurchased) {
          toast.error(`Daily cap (${dailyCap}) cannot be greater than total purchased (${totalPurchased})`);
          setSaving(prev => ({ ...prev, [identifier]: false }));
          return;
        }
        if (monthlyCap > totalPurchased) {
          toast.error(`Monthly cap (${monthlyCap}) cannot be greater than total purchased (${totalPurchased})`);
          setSaving(prev => ({ ...prev, [identifier]: false }));
          return;
        }
      } else if (agent.agentType === 'group' && agent.groupMembers?.length > 0) {
        const totalMemberDailyCap = agent.groupMembers.reduce((sum, m) => sum + (m.dailyCap || 0), 0);
        const totalMemberMonthlyCap = agent.groupMembers.reduce((sum, m) => sum + (m.monthlyCap || 0), 0);
        
        if (totalMemberDailyCap > totalPurchased) {
          toast.error(`Sum of member daily caps (${totalMemberDailyCap}) cannot be greater than total purchased (${totalPurchased})`);
          setSaving(prev => ({ ...prev, [identifier]: false }));
          return;
        }
        if (totalMemberMonthlyCap > totalPurchased) {
          toast.error(`Sum of member monthly caps (${totalMemberMonthlyCap}) cannot be greater than total purchased (${totalPurchased})`);
          setSaving(prev => ({ ...prev, [identifier]: false }));
          return;
        }
      }
      
      // ✅ EXACT SAME APPROACH AS AdminAgents.js - Fetch full agent data first
      const fullAgentResponse = await axios.get(`/api/admin/agents/${encodeURIComponent(agent.email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fullAgent = fullAgentResponse.data;
      
      // Compress group members carrier data to reduce payload size
      let compressedAgent = { ...fullAgent };
      if (fullAgent.groupMembers && fullAgent.groupMembers.length > 0) {
        compressedAgent.groupMembers = fullAgent.groupMembers.map(member => ({
          ...member,
          statesWithCarriers: member.statesWithCarriers ? compressCarrierData(member.statesWithCarriers) : {}
        }));
      }
      
      // Update with the edited config - SAME AS AdminAgents.js
      await axios.put(`/api/admin/agents/${encodeURIComponent(agent.email)}`, {
        ...compressedAgent,
        distributionConfig: {
          ...compressedAgent.distributionConfig,
          priority,
          weightage,
          dailyCap,
          monthlyCap,
          totalPurchased
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update agents array with new values so inputs don't revert
      setAgents(prev => prev.map(a => {
        if (a.email === agent.email) {
          return {
            ...a,
            distributionConfig: {
              ...a.distributionConfig,
              priority,
              weightage,
              dailyCap,
              monthlyCap,
              totalPurchased
            }
          };
        }
        return a;
      }));

      // Clear editing cells for this agent only - USE FUNCTIONAL FORM
      setEditingCells(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (key.startsWith(`${identifier}-`)) {
            delete updated[key];
          }
        });
        return updated;
      });
      
      setSaving(prev => ({ ...prev, [identifier]: false }));
      toast.success('Configuration updated successfully');
    } catch (error) {
      console.error('Error updating config:', error);
      toast.error('Error updating configuration');
      setSaving(prev => ({ ...prev, [identifier]: false }));
    }
  };

  const saveMemberConfig = async (agent, member) => {
    console.log('saveMemberConfig called with agent:', {
      email: agent.email,
      ghlLocationId: agent.ghlLocationId,
      agentType: agent.agentType
    });
    
    if (!agent.email) {
      console.error('Agent has no email field:', agent);
      toast.error('Cannot save: Agent email is missing');
      return;
    }
    
    const identifier = agent.ghlLocationId || agent.email;
    const memberKey = `${identifier}-${member.email}`;
    setSaving(prev => ({ ...prev, [memberKey]: true }));

    try {
      // ✅ EXACT SAME APPROACH AS AdminAgents.js - Fetch full agent data first
      const fullAgentResponse = await axios.get(`/api/admin/agents/${encodeURIComponent(agent.email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fullAgent = fullAgentResponse.data;
      
      // Get edited values for this member
      const priority = parseInt(getCellValue(identifier, 'priority', member.priority || 1, member.email)) || 1;
      const weightage = parseInt(getCellValue(identifier, 'weightage', member.weightage || 1, member.email)) || 1;
      const dailyCap = parseInt(getCellValue(identifier, 'dailyCap', member.dailyCap || 0, member.email)) || 0;
      const monthlyCap = parseInt(getCellValue(identifier, 'monthlyCap', member.monthlyCap || 0, member.email)) || 0;

      // ✅ Validation
      const groupTotalPurchased = fullAgent.distributionConfig?.totalPurchased || 0;
      if (dailyCap > groupTotalPurchased) {
        toast.error(`${member.name}: Daily cap cannot exceed group's total purchased`);
        setSaving(prev => ({ ...prev, [memberKey]: false }));
        return;
      }
      if (monthlyCap > groupTotalPurchased) {
        toast.error(`${member.name}: Monthly cap cannot exceed group's total purchased`);
        setSaving(prev => ({ ...prev, [memberKey]: false }));
        return;
      }

      // ✅ Update member in groupMembers array
      const updatedMembers = fullAgent.groupMembers.map(m => {
        if (m.email === member.email) {
          return {
            ...m,
            priority,
            weightage,
            dailyCap,
            monthlyCap
          };
        }
        return m;
      });

      // ✅ Compress carrier data
      const compressedMembers = updatedMembers.map(member => ({
        ...member,
        statesWithCarriers: member.statesWithCarriers ? compressCarrierData(member.statesWithCarriers) : {}
      }));

      // ✅ Update agent with new member data - SAME AS AdminAgents.js
      await axios.put(`/api/admin/agents/${encodeURIComponent(agent.email)}`, {
        ...fullAgent,
        groupMembers: compressedMembers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update agents array with new member values so inputs don't revert
      setAgents(prev => prev.map(a => {
        if (a.email === agent.email && a.groupMembers) {
          return {
            ...a,
            groupMembers: a.groupMembers.map(m => {
              if (m.email === member.email) {
                return {
                  ...m,
                  priority,
                  weightage,
                  dailyCap,
                  monthlyCap
                };
              }
              return m;
            })
          };
        }
        return a;
      }));
      
      // Clear editing cells for this member only - USE FUNCTIONAL FORM
      setEditingCells(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (key.startsWith(`${identifier}-${member.email}-`)) {
            delete updated[key];
          }
        });
        return updated;
      });
      
      setSaving(prev => ({ ...prev, [memberKey]: false }));
      toast.success(`${member.name} updated successfully`);
    } catch (error) {
      console.error('Error updating member config:', error);
      toast.error('Error updating member configuration');
      setSaving(prev => ({ ...prev, [memberKey]: false }));
    }
  };

  // Toggle collapse/expand for a group's members
  const toggleGroupCollapse = (identifier) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(identifier)) {
        next.delete(identifier);
      } else {
        next.add(identifier);
      }
      return next;
    });
  };

  // Filter and sort agents
  const filteredAndSortedAgents = agents
    .filter(agent => {
      const config = agent.distributionConfig || {};
      const isEnabled = config.enabled !== false;

      if (statusFilter === 'active'   && !isEnabled) return false;
      if (statusFilter === 'inactive' &&  isEnabled) return false;
      if (typeFilter === 'individual' && agent.agentType === 'group') return false;
      if (typeFilter === 'group'      && agent.agentType !== 'group') return false;

      return true;
    })
    .sort((a, b) => {
      const { col, dir } = sortConfig;
      if (!col || !dir) return 0;

      const cfgA = a.distributionConfig || {};
      const cfgB = b.distributionConfig || {};
      const nameA = (a.agentType === 'group' ? a.ghlLocationName : a.name || '').toLowerCase();
      const nameB = (b.agentType === 'group' ? b.ghlLocationName : b.name || '').toLowerCase();

      let valA, valB;
      switch (col) {
        case 'type':
          valA = a.agentType === 'group' ? 0 : 1;
          valB = b.agentType === 'group' ? 0 : 1;
          break;
        case 'name':
          valA = nameA; valB = nameB;
          break;
        case 'status':
          valA = cfgA.enabled !== false ? 0 : 1;
          valB = cfgB.enabled !== false ? 0 : 1;
          break;
        case 'todaySent':
          valA = cfgA.dailySent || 0;
          valB = cfgB.dailySent || 0;
          break;
        case 'yesterdaySent':
          valA = cfgA.yesterdaySent || 0;
          valB = cfgB.yesterdaySent || 0;
          break;
        case 'totalSent':
          valA = (a.ghlContactCount != null ? a.ghlContactCount : cfgA.totalSent) || 0;
          valB = (b.ghlContactCount != null ? b.ghlContactCount : cfgB.totalSent) || 0;
          break;
        case 'priority':
          valA = cfgA.priority || 1;
          valB = cfgB.priority || 1;
          break;
        case 'weightage':
          valA = cfgA.weightage || 1;
          valB = cfgB.weightage || 1;
          break;
        case 'dailyCap':
          valA = cfgA.dailyCap || 0;
          valB = cfgB.dailyCap || 0;
          break;
        case 'monthlyCap':
          valA = cfgA.monthlyCap || 0;
          valB = cfgB.monthlyCap || 0;
          break;
        case 'totalPurchased':
          valA = cfgA.totalPurchased || 0;
          valB = cfgB.totalPurchased || 0;
          break;
        default:
          return 0;
      }

      if (typeof valA === 'string') {
        return dir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      return dir === 'asc' ? valA - valB : valB - valA;
    });

  if (loading) return <div className="loading">Loading configurations...</div>;

  return (
    <div className="configs-management">
      <div className="section-header">
        <h2>Bulk Configuration Management</h2>
        <p className="section-description">
          Edit all agent and group member configurations inline. Changes are saved individually.
        </p>
      </div>

      {/* ── Channel Controls ── */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#111827' }}>📡 Agent Communication Channels</h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
              Control which channels agents can use to communicate with their leads. Chat is always enabled.
            </p>
          </div>
          {channelSaving && <span style={{ fontSize: '12px', color: '#6b7280' }}>Saving…</span>}
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          {/* SMS Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px 20px', flex: 1, minWidth: '240px' }}>
            <span style={{ fontSize: '24px' }}>📱</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>SMS</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                {channelConfig.smsEnabled ? 'Agents can send SMS to leads' : 'SMS is disabled for all agents'}
              </div>
            </div>
            <button
              onClick={() => saveChannelConfig({ ...channelConfig, smsEnabled: !channelConfig.smsEnabled })}
              disabled={channelSaving}
              style={{
                width: '48px', height: '26px', borderRadius: '999px', border: 'none', cursor: 'pointer',
                background: channelConfig.smsEnabled ? '#059669' : '#d1d5db',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                opacity: channelSaving ? 0.6 : 1,
              }}
            >
              <span style={{
                position: 'absolute', top: '3px',
                left: channelConfig.smsEnabled ? '25px' : '3px',
                width: '20px', height: '20px', borderRadius: '50%',
                background: '#fff', transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,.3)',
              }} />
            </button>
          </div>
          {/* Email Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px 20px', flex: 1, minWidth: '240px' }}>
            <span style={{ fontSize: '24px' }}>📧</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>Email</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                {channelConfig.emailEnabled ? 'Agents can send emails to leads' : 'Email is disabled for all agents'}
              </div>
            </div>
            <button
              onClick={() => saveChannelConfig({ ...channelConfig, emailEnabled: !channelConfig.emailEnabled })}
              disabled={channelSaving}
              style={{
                width: '48px', height: '26px', borderRadius: '999px', border: 'none', cursor: 'pointer',
                background: channelConfig.emailEnabled ? '#059669' : '#d1d5db',
                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                opacity: channelSaving ? 0.6 : 1,
              }}
            >
              <span style={{
                position: 'absolute', top: '3px',
                left: channelConfig.emailEnabled ? '25px' : '3px',
                width: '20px', height: '20px', borderRadius: '50%',
                background: '#fff', transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,.3)',
              }} />
            </button>
          </div>
          {/* Chat — always on */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '14px 20px', flex: 1, minWidth: '240px' }}>
            <span style={{ fontSize: '24px' }}>💬</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>Chat</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Always enabled — cannot be disabled</div>
            </div>
            <div style={{ width: '48px', height: '26px', borderRadius: '999px', background: '#059669', position: 'relative', flexShrink: 0 }}>
              <span style={{ position: 'absolute', top: '3px', left: '25px', width: '20px', height: '20px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Type:</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types</option>
              <option value="individual">Individuals Only</option>
              <option value="group">Groups Only</option>
            </select>
          </div>

          <div className="filter-results">
            Showing <strong>{filteredAndSortedAgents.length}</strong> of <strong>{agents.length}</strong> agents
          </div>
        </div>
      </div>

      <div className="configs-table-wrapper">
        <table className="configs-table">
          <thead>
            <tr>
              <th rowSpan="2" onClick={() => handleSort('type')} className="sortable-th">
                Type {sortArrow('type')}
              </th>
              <th rowSpan="2" onClick={() => handleSort('name')} className="sortable-th">
                Name / Email {sortArrow('name')}
              </th>
              <th rowSpan="2" onClick={() => handleSort('status')} className="sortable-th">
                Status {sortArrow('status')}
              </th>
              <th rowSpan="2" onClick={() => handleSort('todaySent')} className="sortable-th">
                Today Sent {sortArrow('todaySent')}
              </th>
              <th rowSpan="2" onClick={() => handleSort('yesterdaySent')} className="sortable-th">
                Yesterday Sent {sortArrow('yesterdaySent')}
              </th>
              <th rowSpan="2" onClick={() => handleSort('totalSent')} className="sortable-th">
                Total Sent / Purchased {sortArrow('totalSent')}
              </th>
              <th colSpan="2">Channels</th>
              <th colSpan="5">Distribution Configuration</th>
            </tr>
            <tr>
              <th style={{ textAlign: 'center' }}>📱 SMS</th>
              <th style={{ textAlign: 'center' }}>📧 Email</th>
              <th onClick={() => handleSort('priority')} className="sortable-th">
                Priority {sortArrow('priority')}
              </th>
              <th onClick={() => handleSort('weightage')} className="sortable-th">
                Weightage {sortArrow('weightage')}
              </th>
              <th onClick={() => handleSort('dailyCap')} className="sortable-th">
                Daily Cap {sortArrow('dailyCap')}
              </th>
              <th onClick={() => handleSort('monthlyCap')} className="sortable-th">
                Monthly Cap {sortArrow('monthlyCap')}
              </th>
              <th onClick={() => handleSort('totalPurchased')} className="sortable-th">
                Total Purchased {sortArrow('totalPurchased')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedAgents.map(agent => {
              const config = agent.distributionConfig || {};
              const isEnabled = config.enabled !== false;
              const identifier = agent.ghlLocationId || agent.email;
              const isSaving = saving[identifier];

              return (
                <React.Fragment key={agent.email}>
                  {/* AGENT/GROUP ROW */}
                  <tr className={`agent-row ${agent.agentType === 'group' ? 'group-row' : ''}`}>
                    <td>
                      {agent.agentType === 'group' ? (
                        <span className="type-badge group-badge">👥 GROUP</span>
                      ) : (
                        <span className="type-badge agent-badge">👤 Agent</span>
                      )}
                    </td>
                    <td className="name-cell">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {agent.agentType === 'group' && agent.groupMembers && agent.groupMembers.length > 0 && (
                          <button
                            onClick={() => toggleGroupCollapse(identifier)}
                            title={collapsedGroups.has(identifier) ? 'Expand members' : 'Collapse members'}
                            style={{
                              background: 'none',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              padding: '1px 5px',
                              fontSize: '12px',
                              color: '#6b7280',
                              lineHeight: 1,
                              flexShrink: 0
                            }}
                          >
                            {collapsedGroups.has(identifier) ? '▶' : '▼'}
                          </button>
                        )}
                        <strong>{agent.agentType === 'group' ? agent.ghlLocationName : agent.name}</strong>
                        {agent.agentType === 'group' && agent.groupMembers && (
                          <span className="member-count">({agent.groupMembers.length} members)</span>
                        )}
                      </div>
                      <div className="email-subtext">
                        {agent.agentType === 'group' ? (
                          <code>{agent.ghlLocationId}</code>
                        ) : (
                          agent.email
                        )}
                      </div>
                    </td>
                    <td>
                      <label style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        position: 'relative'
                      }}>
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={() => toggleAgentStatus(agent)}
                          disabled={saving[`${identifier}-toggle`]}
                          style={{ display: 'none' }}
                        />
                        <div style={{
                          width: '44px',
                          height: '24px',
                          background: isEnabled ? '#10b981' : '#9ca3af',
                          borderRadius: '12px',
                          position: 'relative',
                          transition: 'background 0.3s',
                          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
                          opacity: saving[`${identifier}-toggle`] ? 0.5 : 1
                        }}>
                          <div style={{
                            width: '20px',
                            height: '20px',
                            background: 'white',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: isEnabled ? '22px' : '2px',
                            transition: 'left 0.3s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </label>
                    </td>
                    {/* Today Sent */}
                    <td style={{ textAlign: 'center', fontWeight: '600', color: '#0ea5e9' }}>
                      {config.dailySent || 0}
                    </td>
                    {/* Yesterday Sent */}
                    <td style={{ textAlign: 'center', fontWeight: '600', color: '#8b5cf6' }}>
                      {config.yesterdaySent || 0}
                    </td>
                    {/* Total Sent / Purchased */}
                    <td className="total-leads-cell">
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <span className="total-leads-display">
                          {agent.ghlContactCount !== null && agent.ghlContactCount !== undefined 
                            ? agent.ghlContactCount 
                            : (config.totalSent || 0)
                          } / {config.totalPurchased || 0}
                        </span>
                        <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>
                          {(() => {
                            const sent = agent.ghlContactCount !== null && agent.ghlContactCount !== undefined 
                              ? agent.ghlContactCount 
                              : (config.totalSent || 0);
                            const total = config.totalPurchased || 0;
                            const remaining = Math.max(0, total - sent);
                            return `${remaining} remaining`;
                          })()}
                        </span>
                        {agent.ghlLocationId && agent.ghlContactCount !== null && agent.ghlContactCount !== undefined && (
                          <span style={{ fontSize: '10px', color: '#10b981', fontWeight: '500' }}>
                            ✓ DB-cached
                          </span>
                        )}
                      </div>
                    </td>
                    {/* SMS channel toggle */}
                    <td style={{ textAlign: 'center' }}>
                      <MiniToggle
                        value={(agent.channelConfig?.smsEnabled) !== false}
                        disabled={saving[`${identifier}-channel`]}
                        onChange={() => toggleAgentChannel(agent, 'smsEnabled')}
                      />
                    </td>
                    {/* Email channel toggle */}
                    <td style={{ textAlign: 'center' }}>
                      <MiniToggle
                        value={(agent.channelConfig?.emailEnabled) !== false}
                        disabled={saving[`${identifier}-channel`]}
                        onChange={() => toggleAgentChannel(agent, 'emailEnabled')}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        className="config-input"
                        value={getCellValue(identifier, 'priority', config.priority || 1)}
                        onChange={(e) => handleCellEditWithSave(identifier, 'priority', e.target.value, agent.email)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        className="config-input"
                        value={getCellValue(identifier, 'weightage', config.weightage || 1)}
                        onChange={(e) => handleCellEditWithSave(identifier, 'weightage', e.target.value, agent.email)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        className="config-input"
                        value={getCellValue(identifier, 'dailyCap', config.dailyCap || 0)}
                        onChange={(e) => handleCellEditWithSave(identifier, 'dailyCap', e.target.value, agent.email)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        className="config-input"
                        value={getCellValue(identifier, 'monthlyCap', config.monthlyCap || 0)}
                        onChange={(e) => handleCellEditWithSave(identifier, 'monthlyCap', e.target.value, agent.email)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        className="config-input"
                        value={getCellValue(identifier, 'totalPurchased', config.totalPurchased || 0)}
                        onChange={(e) => handleCellEditWithSave(identifier, 'totalPurchased', e.target.value, agent.email)}
                      />
                    </td>
                  </tr>

                  {/* GROUP MEMBERS - only shown when not collapsed */}
                  {agent.agentType === 'group' && agent.groupMembers && agent.groupMembers.length > 0 && !collapsedGroups.has(identifier) && (
                    agent.groupMembers.map(member => {
                      const memberKey = `${identifier}-${member.email}`;
                      const isMemberSaving = saving[memberKey];
                      const memberEnabled = member.enabled !== false;

                      return (
                        <tr key={memberKey} className="member-row">
                          <td>
                            <span className="type-badge member-badge">└ Member</span>
                          </td>
                          <td className="name-cell member-name">
                            <div>{member.name}</div>
                            <div className="email-subtext">{member.email}</div>
                          </td>
                          <td>
                            <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', position: 'relative' }}>
                              <input
                                type="checkbox"
                                checked={memberEnabled}
                                onChange={() => toggleMemberStatus(agent, member)}
                                disabled={saving[`${identifier}-${member.email}-toggle`]}
                                style={{ display: 'none' }}
                              />
                              <div style={{
                                width: '44px', height: '24px',
                                background: memberEnabled ? '#10b981' : '#9ca3af',
                                borderRadius: '12px', position: 'relative', transition: 'background 0.3s',
                                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
                                opacity: saving[`${identifier}-${member.email}-toggle`] ? 0.5 : 1
                              }}>
                                <div style={{
                                  width: '20px', height: '20px', background: 'white', borderRadius: '50%',
                                  position: 'absolute', top: '2px',
                                  left: memberEnabled ? '22px' : '2px', transition: 'left 0.3s',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                }} />
                              </div>
                            </label>
                          </td>
                          {/* Today Sent */}
                          <td style={{ textAlign: 'center', fontWeight: '600', color: '#0ea5e9' }}>
                            {member.dailySent || 0}
                          </td>
                          {/* Yesterday Sent */}
                          <td style={{ textAlign: 'center', fontWeight: '600', color: '#8b5cf6' }}>
                            {member.yesterdaySent || 0}
                          </td>
                          {/* Total Sent — member's GHL cached count or DB totalSent */}
                          <td className="readonly-cell">
                            <span className="readonly-text" title={member.ghlContactCount !== undefined && member.ghlContactCount !== null ? '🌐 GHL DB-cached' : (member.totalSent !== undefined ? 'DB count' : 'From Group')}>
                              {member.ghlContactCount !== undefined && member.ghlContactCount !== null
                                ? `🌐 ${member.ghlContactCount}`
                                : (member.totalSent !== undefined && member.totalSent !== null
                                  ? member.totalSent
                                  : 'From Group')}
                            </span>
                          </td>
                           {/* SMS channel toggle — member */}
                           <td style={{ textAlign: 'center' }}>
                             <MiniToggle
                               value={(member.channelConfig?.smsEnabled) !== false}
                               disabled={saving[`${identifier}-${member.email}-channel`]}
                               onChange={() => toggleMemberChannel(agent, member, 'smsEnabled')}
                             />
                           </td>
                           {/* Email channel toggle — member */}
                           <td style={{ textAlign: 'center' }}>
                             <MiniToggle
                               value={(member.channelConfig?.emailEnabled) !== false}
                               disabled={saving[`${identifier}-${member.email}-channel`]}
                               onChange={() => toggleMemberChannel(agent, member, 'emailEnabled')}
                             />
                           </td>

                          <td>
                            <input
                              type="number" min="1" className="config-input"
                              value={getCellValue(identifier, 'priority', member.priority || 1, member.email)}
                              onChange={(e) => handleCellEditWithSave(identifier, 'priority', e.target.value, agent.email, member.email)}
                            />
                          </td>
                          <td>
                            <input
                              type="number" min="1" max="10" className="config-input"
                              value={getCellValue(identifier, 'weightage', member.weightage || 1, member.email)}
                              onChange={(e) => handleCellEditWithSave(identifier, 'weightage', e.target.value, agent.email, member.email)}
                            />
                          </td>
                          <td>
                            <input
                              type="number" min="0" className="config-input"
                              value={getCellValue(identifier, 'dailyCap', member.dailyCap || 0, member.email)}
                              onChange={(e) => handleCellEditWithSave(identifier, 'dailyCap', e.target.value, agent.email, member.email)}
                            />
                          </td>
                          <td>
                            <input
                              type="number" min="0" className="config-input"
                              value={getCellValue(identifier, 'monthlyCap', member.monthlyCap || 0, member.email)}
                              onChange={(e) => handleCellEditWithSave(identifier, 'monthlyCap', e.target.value, agent.email, member.email)}
                            />
                          </td>
                          {/* Total Purchased — from group */}
                          <td className="readonly-cell">
                            <span className="readonly-text">From Group</span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {filteredAndSortedAgents.length === 0 && agents.length > 0 && (
          <div className="empty-state">
            No agents match the selected filters
          </div>
        )}

        {agents.length === 0 && (
          <div className="empty-state">
            No agents found
          </div>
        )}
      </div>
    </div>
  );
}

function MiniToggle({ value, onChange, disabled }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      title={value ? 'Enabled — click to disable' : 'Disabled — click to enable'}
      style={{
        width: '36px', height: '20px', borderRadius: '999px', border: 'none',
        cursor: disabled ? 'wait' : 'pointer',
        background: value ? '#059669' : '#d1d5db',
        position: 'relative', transition: 'background 0.2s',
        opacity: disabled ? 0.5 : 1, flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: '2px',
        left: value ? '18px' : '2px',
        width: '16px', height: '16px', borderRadius: '50%',
        background: '#fff', transition: 'left 0.2s',
        boxShadow: '0 1px 2px rgba(0,0,0,.3)',
      }} />
    </button>
  );
}

export default AdminConfigsManagement;
